import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CardMedia } from "@mui/material";
import {
  getAuth,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import { functions } from "../firebase";
import { T } from "./Translate";
import logo from "../imgs/logo192.png";

// Segundo factor NATIVO de Firebase Auth, distinto del 2FA propio de la app
// (ver TwoFactorChallenge.js, que corre después de este). Se dispara cuando
// signInWithEmailAndPassword rechaza el login con
// auth/multi-factor-auth-required. El `resolver` (obtenido con
// getMultiFactorResolver, SDK modular — la API de TOTP no existe en
// firebase/compat) trae los factores enrolados; hay que completar la prueba
// de posesión para terminar de loguear. Soporta TOTP (app autenticadora,
// sin paso de envío) y teléfono (SMS, con reCAPTCHA) según lo que tenga
// enrolado la cuenta.
const MfaChallenge = ({ resolver, email, password, onSuccess, onRecovered, onCancel }) => {
  const [step, setStep] = useState("send"); // send | verify (solo aplica a phone)
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [useSms, setUseSms] = useState(false); // con ambos factores, por defecto va la app autenticadora
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  // Recuperación cuando se perdió el acceso al segundo factor: valida un
  // código de respaldo en el servidor (useMfaBackupCode) y, si es válido,
  // desactiva el MFA nativo de la cuenta para poder loguearse de nuevo.
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const [recovering, setRecovering] = useState(false);

  const handleRecover = async (e) => {
    e.preventDefault();
    setRecoveryError("");
    setRecovering(true);
    try {
      const callUseMfaBackupCode = functions.httpsCallable("useMfaBackupCode");
      await callUseMfaBackupCode({ email, password, backupCode: recoveryCode.trim() });
      onRecovered();
    } catch (err) {
      setRecoveryError(err.message || "No pudimos validar el código de respaldo.");
    }
    setRecovering(false);
  };

  const totpFactor = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  const phoneFactor = resolver.hints.find((h) => h.factorId === "phone");
  const totpHint = useSms ? null : totpFactor;
  const phoneHint = !totpHint ? phoneFactor : null;

  const switchFactor = (sms) => {
    setUseSms(sms);
    setStep("send");
    setCode("");
    setError("");
  };

  useEffect(() => {
    if (totpHint) {
      setStep("verify");
      return undefined;
    }
    if (!phoneHint || !recaptchaContainerRef.current) return undefined;
    recaptchaVerifierRef.current = new RecaptchaVerifier(getAuth(), recaptchaContainerRef.current, {
      size: "invisible",
    });
    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    };
  }, [totpHint, phoneHint]);

  const handleSendCode = async () => {
    setError("");
    setLoading(true);
    try {
      const phoneAuthProvider = new PhoneAuthProvider(getAuth());
      const id = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        recaptchaVerifierRef.current
      );
      setVerificationId(id);
      setStep("verify");
    } catch (err) {
      setError("No pudimos enviar el código: " + err.message);
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const assertion = totpHint
        ? TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim())
        : PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code.trim()));
      const result = await resolver.resolveSignIn(assertion);
      onSuccess(result);
    } catch (err) {
      // Firebase reutiliza auth/invalid-verification-code (con su mensaje
      // orientado a SMS) también para TOTP inválido/expirado — mostrar el
      // err.message tal cual acá confunde al usuario de app autenticadora.
      const message = totpHint
        ? "El código no es válido o ya expiró. Generá uno nuevo desde tu app autenticadora e intentá de nuevo."
        : "Código inválido: " + err.message;
      setError(message);
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        gap: 2,
        textAlign: "center",
      }}
    >
      <CardMedia component="img" image={logo} sx={{ width: 72, height: 72, mb: 1 }} />
      <Typography variant="h6"><T>Verificación en dos pasos</T></Typography>

      {!totpHint && !phoneHint ? (
        <Typography color="error" sx={{ maxWidth: 320 }}>
          <T>Tu cuenta tiene un segundo factor de un tipo que esta versión de la app todavía no soporta. Contactá a soporte.</T>
        </Typography>
      ) : totpHint ? (
        <form onSubmit={handleVerify} style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320 }}>
            <T>Ingresá el código de tu app autenticadora.</T>
          </Typography>
          <input
            type="text"
            autoFocus
            inputMode="numeric"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc", width: 200, textAlign: "center", fontSize: 20, letterSpacing: 2 }}
          />
          <button
            type="submit"
            disabled={loading || !code}
            style={{ color: "white", background: "#1976d2", padding: "10px 24px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold" }}
          >
            <T>Verificar</T>
          </button>
        </form>
      ) : step === "send" ? (
        <>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320 }}>
            <T>Te vamos a enviar un código por SMS al número terminado en</T> {phoneHint.phoneNumber?.slice(-4)}.
          </Typography>
          <button
            onClick={handleSendCode}
            disabled={loading}
            style={{ color: "white", background: "#1976d2", padding: "10px 24px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold" }}
          >
            <T>Enviar código</T>
          </button>
        </>
      ) : (
        <form onSubmit={handleVerify} style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320 }}>
            <T>Ingresá el código que te llegó por SMS.</T>
          </Typography>
          <input
            type="text"
            autoFocus
            inputMode="numeric"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc", width: 200, textAlign: "center", fontSize: 20, letterSpacing: 2 }}
          />
          <button
            type="submit"
            disabled={loading || !code}
            style={{ color: "white", background: "#1976d2", padding: "10px 24px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold" }}
          >
            <T>Verificar</T>
          </button>
        </form>
      )}

      {totpFactor && phoneFactor && (
        <Typography
          variant="body2"
          onClick={() => switchFactor(!useSms)}
          sx={{ color: "text.secondary", textDecoration: "underline", cursor: "pointer" }}
        >
          <T>{useSms ? "Usar mi app autenticadora" : "Recibir un código por SMS"}</T>
        </Typography>
      )}

      {error && <Typography color="error" variant="body2" sx={{ maxWidth: 320 }}>{error}</Typography>}
      <div ref={recaptchaContainerRef} />

      {email && password && (showRecovery ? (
        <form onSubmit={handleRecover} style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", marginTop: 8 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 320 }}>
            <T>Ingresá uno de tus códigos de respaldo para desactivar la verificación en dos pasos y volver a entrar.</T>
          </Typography>
          <input
            type="text"
            autoFocus
            placeholder="XXXX-XXXX"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #ccc", width: 200, textAlign: "center", fontSize: 18, letterSpacing: 1 }}
          />
          <button
            type="submit"
            disabled={recovering || !recoveryCode}
            style={{ color: "white", background: "#1976d2", padding: "10px 24px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold" }}
          >
            <T>Usar código de respaldo</T>
          </button>
          {recoveryError && <Typography color="error" variant="body2" sx={{ maxWidth: 320 }}>{recoveryError}</Typography>}
        </form>
      ) : (
        <Typography
          variant="body2"
          onClick={() => setShowRecovery(true)}
          sx={{ color: "text.secondary", textDecoration: "underline", cursor: "pointer", mt: 2 }}
        >
          <T>¿No podés usar tu app autenticadora ni recibir el SMS?</T>
        </Typography>
      ))}

      <Typography
        variant="body2"
        onClick={onCancel}
        sx={{ color: "text.secondary", textDecoration: "underline", cursor: "pointer", mt: 2 }}
      >
        <T>Cancelar</T>
      </Typography>
    </Box>
  );
};

export default MfaChallenge;
